import {
  Button,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  NativeSelect,
} from '@eureca/ui';
import { XIcon } from './icons';
import type { SheetSelectOption } from './PhaseSheet';

export interface PhaseDraft {
  name: string;
  type: string;
  /** ISO `YYYY-MM-DD` ou string vazia. Exibido no card como "Começa". */
  startDate: string;
  /** ISO `YYYY-MM-DD` ou string vazia. Exibido como "Encerra". */
  endDate: string;
  /** `HH:MM` (24h). Default `'23:59'` (EU-165 RN-05). */
  endTime: string;
}

export type PhaseDialogMode = 'add' | 'edit';

export interface PhaseAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: PhaseDraft;
  errors: Record<string, string>;
  isSubmitting: boolean;
  typeOptions: SheetSelectOption[];
  onFieldChange: <K extends keyof PhaseDraft>(field: K, value: PhaseDraft[K]) => void;
  onSubmit: () => void;
  mode?: PhaseDialogMode;
}

export function PhaseAddDialog({
  open,
  onOpenChange,
  values,
  errors,
  isSubmitting,
  typeOptions,
  onFieldChange,
  onSubmit,
  mode = 'add',
}: PhaseAddDialogProps) {
  const isEdit = mode === 'edit';
  const title = isEdit ? 'Editar fase' : 'Adicionar nova fase';
  const submitLabel = isEdit ? 'Salvar alterações' : 'Adicionar fase';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn('gap-0 p-0', 'lg:w-full lg:max-w-[423px] lg:rounded-xl')}
      >
        <header className="flex flex-row items-start gap-3 px-6 pt-6">
          <DialogTitle className="flex-1 text-lg font-semibold leading-none tracking-tight text-foreground">
            {title}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Fechar"
              className="inline-flex size-4 items-center justify-center text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid="phase-add-dialog-close"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <form
          className="flex flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
          noValidate
        >
          <div className="flex flex-col gap-4 px-6 pt-3 pb-8">
            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel className="text-foreground">Nome</FormFieldLabel>
              <Input
                value={values.name}
                placeholder="Ex.: Informações Adicionais"
                variant={errors.name ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('name', event.target.value)}
                data-testid="phase-add-dialog-name"
              />
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.name}
              </FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.type)}>
              <FormFieldLabel className="text-foreground">Tipo</FormFieldLabel>
              <NativeSelect
                value={values.type}
                onChange={(event) => onFieldChange('type', event.target.value)}
                data-testid="phase-add-dialog-type"
              >
                <option value="" disabled>
                  Selecione uma opção
                </option>
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelect>
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.type}
              </FormFieldError>
              {/* Passagem automática — fases desses tipos enviam os candidatos
                  movimentados para o App de Gestores (subscriber no Core). */}
              {values.type === 'dinamica' || values.type === 'entrevista' ? (
                <FormFieldHint
                  data-testid="phase-add-dialog-manager-hint"
                  className="text-sm leading-5 text-muted-foreground"
                >
                  Candidatos movimentados para fases de Dinâmica ou Entrevista são enviados
                  automaticamente para o App de Gestores.
                </FormFieldHint>
              ) : null}
            </FormField>

            {/* EU-165 Juliana TODO #6 / B7 — Figma define que o modal de fase
                contém APENAS Nome + Tipo. Datas e horário ficam ao nível da
                etapa (PhaseSheet), onde já existem. Os campos `startDate`,
                `endDate`, `endTime` continuam no draft pra preservar valores
                de vagas legadas que já tinham datas no nível da fase, mas
                não são mais editáveis aqui. */}
          </div>

          <footer className="flex flex-row justify-end gap-2 px-6 pb-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Voltar
              </Button>
            </DialogClose>
            <Button type="submit" loading={isSubmitting} data-testid="phase-add-dialog-submit">
              {submitLabel}
            </Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}
